import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink } from 'lucide-react';
import { SiWhatsapp, SiYoutube, SiInstagram, SiLinkedin } from 'react-icons/si';
import { ROUTE_PATHS, SOCIAL_LINKS } from '@/lib/index';
import { ChatbotPopup } from './ChatbotPopup';

const NAV_LINKS = [
  { label: 'Início', href: '/#hero' },
  { label: 'Sobre', href: '/#sobre' },
  { label: 'Metodologia', href: '/#metodologia' },
  { label: 'Serviços', href: '/#servicos' },
  { label: 'Blog', href: ROUTE_PATHS.BLOG },
  { label: 'Contato', href: '/#contato' },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const isInternalAnchor = (href: string) => href.startsWith('/#');
  const isBlogPage = location.pathname.startsWith('/blog');

  const handleAnchorClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    if (isInternalAnchor(href)) {
      e.preventDefault();
      const anchor = href.replace('/#', '');
      if (isBlogPage) {
        // navigate to home then scroll
        window.location.href = href;
      } else {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }
      setMobileOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* HEADER */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || mobileOpen
            ? 'bg-background/95 border-b border-border'
            : 'bg-transparent'
        }`}
        style={{ backdropFilter: isScrolled || mobileOpen ? 'blur(12px)' : 'none' }}
      >
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-2 group">
            <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
              <span className="text-primary font-bold text-sm font-mono">KS</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-foreground text-sm">Kamillo Santos</span>
              <span className="text-primary/70 text-xs ml-1 font-mono">nexus</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) =>
              isInternalAnchor(link.href) ? (
                <a
                  key={link.label}
                  href={link.href}
                  onClick={(e) => handleAnchorClick(e, link.href)}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200 font-medium"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`text-sm font-medium transition-colors duration-200 ${
                    location.pathname === link.href
                      ? 'text-primary'
                      : 'text-muted-foreground hover:text-primary'
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href={SOCIAL_LINKS.WHATSAPP}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <SiWhatsapp className="w-4 h-4" />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className="md:hidden p-2 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-border bg-background/95"
            >
              <nav className="container mx-auto px-4 py-4 flex flex-col gap-3">
                {NAV_LINKS.map((link) =>
                  isInternalAnchor(link.href) ? (
                    <a
                      key={link.label}
                      href={link.href}
                      onClick={(e) => handleAnchorClick(e, link.href)}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors py-2 font-medium"
                    >
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      key={link.label}
                      to={link.href}
                      className={`text-sm py-2 font-medium transition-colors ${
                        location.pathname === link.href
                          ? 'text-primary'
                          : 'text-muted-foreground hover:text-primary'
                      }`}
                    >
                      {link.label}
                    </Link>
                  )
                )}
                <a
                  href={SOCIAL_LINKS.WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 rounded-lg text-sm font-semibold justify-center mt-2"
                >
                  <SiWhatsapp className="w-4 h-4" />
                  Falar no WhatsApp
                </a>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* MAIN */}
      <main>{children}</main>

      {/* Chatbot Popup */}
      <ChatbotPopup />

      {/* FOOTER */}
      <footer className="border-t border-border bg-card">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/20 border border-primary/40 flex items-center justify-center">
                  <span className="text-primary font-bold text-sm font-mono">KS</span>
                </div>
                <span className="font-bold text-lg">Kamillo Santos</span>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed max-w-sm">
                Engenheiro de Tráfego e Especialista em IA Preditiva. Sistema Nexus Maximus para ROI 100x. Salvador, Bahia.
              </p>
              <div className="flex items-center gap-3 mt-4">
                <a
                  href={SOCIAL_LINKS.WHATSAPP}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 hover:text-primary border border-border flex items-center justify-center transition-colors"
                  aria-label="WhatsApp"
                >
                  <SiWhatsapp className="w-4 h-4" />
                </a>
                <a
                  href={SOCIAL_LINKS.YOUTUBE}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 hover:text-primary border border-border flex items-center justify-center transition-colors"
                  aria-label="YouTube"
                >
                  <SiYoutube className="w-4 h-4" />
                </a>
                <a
                  href={SOCIAL_LINKS.LINKEDIN}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 hover:text-primary border border-border flex items-center justify-center transition-colors"
                  aria-label="LinkedIn"
                >
                  <SiLinkedin className="w-4 h-4" />
                </a>
                <a
                  href={SOCIAL_LINKS.INSTAGRAM}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-lg bg-muted hover:bg-primary/10 hover:text-primary border border-border flex items-center justify-center transition-colors"
                  aria-label="Instagram"
                >
                  <SiInstagram className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="font-semibold text-sm mb-4 text-foreground">Navegação</h4>
              <ul className="space-y-2">
                {NAV_LINKS.map((link) => (
                  <li key={link.label}>
                    {isInternalAnchor(link.href) ? (
                      <a
                        href={link.href}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.label}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className="text-sm text-muted-foreground hover:text-primary transition-colors"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {/* Contato */}
            <div>
              <h4 className="font-semibold text-sm mb-4 text-foreground">Contato</h4>
              <ul className="space-y-2">
                <li className="text-sm text-muted-foreground">Salvador, Bahia — Brasil</li>
                <li>
                  <a
                    href={SOCIAL_LINKS.WHATSAPP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    WhatsApp <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${SOCIAL_LINKS.EMAIL}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors"
                  >
                    {SOCIAL_LINKS.EMAIL}
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              © 2026 Kamillo Santos. Todos os direitos reservados.
            </p>
            <p className="text-xs text-muted-foreground font-mono">
              Sistema Nexus Maximus v2.0 — ROI Preditivo
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
